import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, HeartPulse, Stethoscope, Users, Accessibility, HandHeart, ShieldPlus, Home as HomeIcon, LayoutGrid, ShieldCheck, Clock, Layers, Handshake,
  Fingerprint, ScanSearch, FileBadge, GraduationCap, BadgeCheck, LineChart, MapPin, Search, Calendar, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogoSymbol, Wordmark, LogoMark } from "@/components/Logo";
import { toast } from "sonner";
import { api, errMsg, useCatalog, SEARCH_KEY } from "@/lib/api";

const CATEGORIES = [
  [HeartPulse, "Home Care", "home_care"], [Stethoscope, "Nursing", "home_health"], [Users, "Behavioral Health", "behavioral_health"], [Car, "Transportation", "nemt"],
  [HandHeart, "Disability Support", "disability_services"], [ShieldPlus, "Therapy & Rehabilitation", "therapy"], [HomeIcon, "Senior Living", "assisted_living"], [LayoutGrid, "And More", null]];
const JOURNEYS = [
  { key: "care", icon: HandHeart, title: "I Need Care", body: "Find trusted care providers currently accepting clients.", cta: "Find Care", to: "/find-care", img: "/images/family-seniors.jpg" },
  { key: "work", icon: BadgeCheck, title: "I'm Looking for Work", body: "Create a professional profile, complete Zunvel verification, and receive healthcare job opportunities.", cta: "Find Jobs", to: "/register?role=worker", img: "/images/svc-nursing.jpg" },
  { key: "provider", icon: Handshake, title: "I'm a Care Provider", body: "Find clients, recruit vetted workers, and grow your healthcare organization.", cta: "Get Started", to: "/register?role=provider", img: "/images/provider-team.jpg" },
];
const WHY = [[ShieldCheck, "Reviewed Credentials", "Worker credentials reviewed by Zunvel staff, category by category."], [Clock, "Real-Time Availability", "See which providers are accepting clients now."],
  [Layers, "All in One Place", "Care, jobs and business growth in one platform."], [Handshake, "Built for Communities", "Stronger care. More opportunities. Better lives."]];
const SERVICES = [["Home Care", "/images/svc-home-care.jpg", "home_care"], ["Nursing", "/images/svc-nursing.jpg", "home_health"], ["Behavioral Health", "/images/svc-behavioral.jpg", "behavioral_health"], ["NEMT Transportation", "/images/svc-transport.jpg", "nemt"]];
const VCATS = [[Fingerprint, "Identity Verified"], [ScanSearch, "Background Screening Complete"], [FileBadge, "Credentials Verified"], [BadgeCheck, "Professional License Verified"], [GraduationCap, "Training Verified"]];

const GoldBtn = ({ to, children, testid, className = "" }) => (
  <Button asChild size="lg" className={`rounded-lg h-12 px-7 zv-btn-gold font-bold text-base ${className}`} data-testid={testid}><Link to={to}>{children} <ArrowRight className="h-4 w-4 ml-2" /></Link></Button>
);
const GreenBtn = ({ to, children, testid }) => (
  <Button asChild className="rounded-lg h-11 px-8 bg-[#003B2D] hover:bg-[#00281F] text-[#FAF8F2] font-semibold" data-testid={testid}><Link to={to}>{children} <ArrowRight className="h-4 w-4 ml-2" /></Link></Button>
);
const Eyebrow = ({ children, light }) => <div className={`text-xs sm:text-sm font-bold uppercase tracking-[0.22em] ${light ? "text-[#E6C77B]" : "text-[#A8853A]"}`}>{children}</div>;

function HeroSearch() {
  const { data: cat } = useCatalog();
  const nav = useNavigate();
  const [zip, setZip] = useState("");
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = /^\d{5}$/.test(zip) && type;
  const go = async (e) => {
    e.preventDefault();
    const c = cat?.service_categories.find((x) => x.key === type);
    if (!c || !ready) return;
    setBusy(true);
    try {
      const { data } = await api.post("/care-search", { zip, care_type_id: c.id, state_code: "PA" });
      localStorage.setItem(SEARCH_KEY, data.id);
      nav(`/find-care/results/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <form onSubmit={go} className="cz-card bg-white p-4 sm:p-5 mt-6 max-w-xl" data-testid="hero-care-search">
      <div className="font-display text-lg font-semibold text-[#102C25]">Find care near you</div>
      <div className="grid sm:grid-cols-[1.4fr_1fr_auto] gap-2.5 mt-3">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-12 bg-[#FAF8F2] text-base" aria-label="Type of care" data-testid="hero-search-type"><SelectValue placeholder="Type of care" /></SelectTrigger>
          <SelectContent>{(cat?.service_categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative"><MapPin className="h-4 w-4 absolute left-3 top-4 text-[#626A5F]" />
          <Input className="h-12 pl-9 bg-[#FAF8F2] text-base" inputMode="numeric" maxLength={5} placeholder="ZIP code" aria-label="ZIP code" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} data-testid="hero-search-zip" /></div>
        <Button type="submit" disabled={!ready || busy} className="h-12 rounded-lg zv-btn-gold font-bold text-base px-6" data-testid="hero-search-submit">{busy ? "Matching…" : "Find matches"}</Button>
      </div>
      <p className="text-xs text-[#46534A] mt-2.5">Free · No account needed to see your matches · <Link to={`/find-care${zip ? `?zip=${zip}` : ""}${type ? `${zip ? "&" : "?"}type=${type}` : ""}`} className="font-semibold text-[#85662A] underline" data-testid="hero-full-questionnaire-link">Answer more questions for better matches</Link></p>
    </form>
  );
}

function Hero() {
  return (
    <section className="relative bg-[#FAF8F2] overflow-hidden" data-testid="hero-section">
      <div className="grid lg:grid-cols-12 lg:min-h-[620px]">
        <div className="lg:col-span-5 relative z-10 px-5 sm:px-10 lg:pl-16 xl:pl-24 py-7 sm:py-12 lg:py-16 flex flex-col justify-center cz-rise">
          <Eyebrow>Your Healthcare Marketplace</Eyebrow>
          <h1 className="text-[2.1rem] sm:text-5xl lg:text-6xl font-bold leading-[1.06] text-[#102C25] mt-3 sm:mt-4" data-testid="hero-title">Find Care. Find Work. <span className="lg:block">Grow Your Care Business.</span></h1>
          <p className="text-base sm:text-lg text-[#2F4239] mt-3 sm:mt-5 max-w-md leading-relaxed" data-testid="hero-tagline">Connecting individuals, providers and healthcare workers for a stronger, healthier tomorrow.</p>
          <HeroSearch />
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm font-semibold">
            <Link to="/jobs" className="inline-flex items-center gap-1 py-2 text-[#003B2D] hover:text-[#85662A]" data-testid="hero-find-jobs-link">Looking for work? Find Jobs <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/register?role=provider" className="inline-flex items-center gap-1 py-2 text-[#003B2D] hover:text-[#85662A]" data-testid="hero-provider-link">Care provider? Get Started <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
        <div className="lg:col-span-7 relative min-h-[230px] sm:min-h-[340px] lg:min-h-0">
          <img src="/images/zunvel-hero.jpg" alt="Zunvel caregiver sharing a warm moment with an older woman" className="absolute inset-0 h-full w-full object-cover lg:[clip-path:polygon(14%_0,100%_0,100%_100%,0_100%)]" />
          <div className="absolute bottom-0 right-0 w-[78%] sm:w-[62%] h-[46%] bg-[#00281F] [clip-path:polygon(100%_0,100%_100%,0_100%)]" />
          <div className="absolute bottom-0 right-0 w-[80%] sm:w-[64%] h-[48%] bg-gradient-to-br from-[#E6C77B] to-[#A9863F] [clip-path:polygon(100%_0,100%_3%,3%_100%,0_100%)]" />
          <div className="absolute bottom-3 right-4 sm:bottom-10 sm:right-12 text-right font-display leading-tight" data-testid="hero-overlay-text">
            {["People", "Care", "Opportunity"].map((t) => <div key={t} className="text-[#FAF8F2] text-sm sm:text-3xl tracking-[0.12em] uppercase">{t}</div>)}
            <div className="text-[#E6C77B] text-sm sm:text-3xl tracking-[0.12em] uppercase">All in one place</div>
          </div>
        </div>
      </div>
    </section>
  );
}

const CategoryStrip = () => (
  <section className="bg-[#003B2D] border-y border-[#D4AF63]/25" data-testid="category-strip">
    <div className="max-w-7xl mx-auto grid grid-cols-4 lg:grid-cols-8">
      {CATEGORIES.map(([I, t, key], i) => (
        <Link key={t} to={key ? `/find-care?type=${key}` : "/providers"} data-testid={`category-${key || "more"}`}
          className={`group flex flex-col items-center justify-center gap-2.5 py-6 px-2 text-center hover:bg-white/5 transition-colors ${i % 4 ? "border-l" : ""} ${i >= 4 ? "border-t lg:border-t-0" : ""} ${i === 4 ? "lg:border-l" : ""} border-[#D4AF63]/20`}>
          <I className="h-8 w-8 sm:h-9 sm:w-9 text-[#D4AF63] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#FAF8F2]">{t}</span>
        </Link>
      ))}
    </div>
  </section>
);

const Journeys = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20 grid md:grid-cols-3 gap-6 lg:gap-8" data-testid="journeys">
    {JOURNEYS.map(({ key, icon: I, title, body, cta, to, img }, i) => (
      <div key={key} className={`cz-card cz-card-hover overflow-hidden flex flex-col bg-[#FFFDF8] cz-rise cz-d${i + 1}`} data-testid={`journey-${key}`}>
        <div className="h-52 overflow-hidden"><img src={img} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" /></div>
        <div className="relative px-6 pb-7 pt-10 flex-1 flex flex-col">
          <span className="absolute -top-9 left-6 h-[72px] w-[72px] rounded-full bg-[#F6ECD2] border-4 border-[#FFFDF8] grid place-items-center shadow-md"><I className="h-8 w-8 text-[#A8853A]" strokeWidth={1.6} /></span>
          <h3 className="font-display text-2xl font-semibold text-[#102C25]">{title}</h3>
          <p className="text-[#46534A] mt-2 flex-1">{body}</p>
          <div className="mt-6"><GreenBtn to={to} testid={`journey-${key}-btn`}>{cta}</GreenBtn></div>
        </div>
      </div>
    ))}
  </section>
);

const Why = () => (
  <section className="bg-[#003B2D] relative overflow-hidden" data-testid="why-zunvel">
    <div className="absolute -left-24 -top-24 h-64 w-64 bg-gradient-to-br from-[#E6C77B] to-[#A9863F] rotate-45 opacity-80" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 relative text-center">
      <h2 className="font-display text-3xl sm:text-5xl font-semibold text-[#FAF8F2]">Why Choose Zunvel?</h2>
      <p className="text-[#FAF8F2]/80 mt-4 max-w-xl mx-auto text-lg">A modern healthcare marketplace built to make care, employment and business growth simple.</p>
      <div className="h-px w-20 bg-[#D4AF63] mx-auto mt-6" />
      <div className="grid grid-cols-2 lg:grid-cols-4 mt-12">
        {WHY.map(([I, t, b], i) => (
          <div key={t} className={`px-4 py-6 ${i ? "lg:border-l" : ""} ${i % 2 ? "border-l" : ""} border-[#D4AF63]/25`}>
            <I className="h-12 w-12 text-[#D4AF63] mx-auto" strokeWidth={1.3} />
            <div className="font-display text-lg sm:text-xl text-[#FAF8F2] mt-4 font-semibold">{t}</div>
            <p className="text-sm text-[#FAF8F2]/70 mt-2">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Services = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20" data-testid="popular-services">
    <div className="flex items-end justify-between"><h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#102C25]">Popular Services</h2>
      <Link to="/providers" className="text-sm font-bold text-[#003B2D] hover:text-[#A8853A] flex items-center gap-1" data-testid="services-view-all">View All <ArrowRight className="h-4 w-4" /></Link></div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
      {SERVICES.map(([t, img, key]) => (
        <Link key={t} to={`/find-care?type=${key}`} className="cz-card cz-card-hover overflow-hidden group" data-testid={`service-card-${key}`}>
          <div className="aspect-[4/3] overflow-hidden"><img src={img} alt={t} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
          <div className="p-4 font-semibold text-[#102C25] flex items-center justify-between">{t}<ArrowRight className="h-4 w-4 text-[#A8853A]" /></div>
        </Link>
      ))}
    </div>
  </section>
);

const Verified = () => (
  <section id="verified" className="bg-[#00281F] text-[#FAF8F2] relative overflow-hidden">
    <div className="absolute inset-0 cz-passport-lines" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-24 relative grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
      <div>
        <Eyebrow light>Zunvel Verified™</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-4">Verification you can actually read.</h2>
        <p className="text-base sm:text-lg text-[#FAF8F2]/75 mt-4 leading-relaxed">Not one vague badge. Zunvel tracks each verification category separately — with its own status, review date, expiration and audit history — reviewed by Zunvel staff.</p>
        <div className="mt-8"><GoldBtn to="/register?role=worker" testid="section-verified-btn">Get verified</GoldBtn></div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="verified-categories">
        {VCATS.map(([I, t], i) => (
          <div key={t} className={`rounded-xl sm:rounded-2xl bg-white/5 border border-[#D4AF63]/20 p-3 sm:p-5 flex sm:block items-center gap-2.5 ${i === 4 ? "col-span-2" : ""}`}>
            <I className="h-5 w-5 text-[#D4AF63] shrink-0" /><div className="font-semibold text-sm sm:text-base sm:mt-3 leading-snug">{t}</div><div className="hidden sm:block text-xs text-[#FAF8F2]/60 mt-1">Status · Date verified · Expiration · Reviewer</div>
          </div>
        ))}
        <details className="sm:hidden col-span-2 text-sm text-[#FAF8F2]/75" data-testid="verified-details">
          <summary className="cursor-pointer text-[#E6C77B] font-semibold py-2">What's tracked for each category?</summary>
          Status, date verified, expiration date, supporting document, reviewer, review notes and a full audit history.
        </details>
      </div>
    </div>
  </section>
);

const Passport = () => (
  <section id="passport" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
    <div className="cz-passport rounded-3xl p-8 text-[#FAF8F2] shadow-2xl relative overflow-hidden max-w-lg border border-[#D4AF63]/30">
      <div className="absolute inset-0 cz-passport-lines" />
      <div className="relative flex items-center justify-between"><span className="text-[11px] uppercase tracking-[0.25em] text-[#E6C77B] font-bold">Zunvel Passport™</span><LogoMark size={40} /></div>
      <div className="relative font-display text-2xl font-semibold mt-6">Your name here</div>
      <div className="relative text-[#FAF8F2]/70 text-sm">Certified Nursing Assistant</div>
      <div className="relative grid grid-cols-5 gap-2 mt-8">{VCATS.map(([I, t]) => <div key={t} className="rounded-xl bg-white/10 p-3 grid place-items-center" title={t}><I className="h-4 w-4 text-[#E6C77B]" /></div>)}</div>
      <div className="relative font-mono-cz text-xs tracking-widest text-[#E6C77B] mt-6">ZVP-XXXX-XXXX</div>
    </div>
    <div>
      <Eyebrow>Zunvel Passport™</Eyebrow>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#102C25] mt-4">Verify once. Apply everywhere.</h2>
      <p className="text-lg text-[#46534A] mt-4 leading-relaxed">Your Passport keeps credentials, licenses and training in one reusable profile. Providers you apply to see verification results and dates — never your confidential background reports.</p>
      <div className="mt-8 flex flex-wrap gap-3"><GreenBtn to="/register?role=worker" testid="section-passport-btn">Build my Passport</GreenBtn><GreenBtn to="/jobs" testid="section-jobs-btn">Browse healthcare jobs</GreenBtn></div>
    </div>
  </section>
);

const Providers = () => (
  <section id="for-providers" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
    <div className="rounded-[2rem] bg-[#FFFDF8] border border-[#E8E1CF] p-8 sm:p-14">
      <Eyebrow>For Providers</Eyebrow>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#102C25] mt-4 max-w-2xl">Grow your care business with one storefront.</h2>
      <div className="grid md:grid-cols-3 gap-6 mt-10">
        {[[Users, "Find clients", "Receive care requests from families in your service area. Turn Accepting New Clients on or off anytime."],
          [ShieldCheck, "Recruit vetted workers", "Post jobs and review applicants' Zunvel Passport™ verification results and dates."],
          [LineChart, "Grow your organization", "Storefront, analytics and workforce tools with Free, Pro and Enterprise plans."]].map(([I, t, b]) => (
          <div key={t} className="cz-card p-6"><I className="h-7 w-7 text-[#A8853A]" strokeWidth={1.6} /><div className="font-display font-semibold text-xl mt-4 text-[#102C25]">{t}</div><p className="text-[#46534A] mt-2 text-sm">{b}</p></div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-3"><GoldBtn to="/register?role=provider" testid="section-providers-btn">List your organization free</GoldBtn><GreenBtn to="/providers" testid="section-storefronts-btn">See provider storefronts</GreenBtn></div>
    </div>
  </section>
);

function Join() {
  const nav = useNavigate();
  const [zip, setZip] = useState("");
  const [type, setType] = useState("");
  return (
    <section className="bg-[#003B2D] relative overflow-hidden" data-testid="join-section">
      <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-bl from-[#D4AF63]/25 to-transparent [clip-path:polygon(40%_0,100%_0,100%_100%,0_100%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center relative">
        <div className="mx-auto w-full max-w-[340px] rounded-[2.6rem] bg-[#0D1512] p-3 shadow-2xl">
          <div className="rounded-[2.1rem] bg-[#FAF8F2] overflow-hidden">
            <div className="bg-[#003B2D] px-5 py-4 flex items-center gap-2"><LogoSymbol size={28} /><Wordmark light size="text-lg" /></div>
            <form className="p-5 space-y-3" onSubmit={(e) => { e.preventDefault(); nav(`/find-care?zip=${zip}${type ? `&type=${type}` : ""}`); }} data-testid="quick-search-form">
              <div className="font-display text-2xl font-semibold text-[#102C25] leading-tight">What kind of help do you need?</div>
              <div className="relative"><MapPin className="h-4 w-4 absolute left-3 top-3.5 text-[#626A5F]" /><Input className="h-11 pl-9 bg-white" inputMode="numeric" maxLength={5} placeholder="Zip Code" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} data-testid="quick-search-zip" /></div>
              <Select value={type} onValueChange={setType}><SelectTrigger className="h-11 bg-white" data-testid="quick-search-type"><SelectValue placeholder="Type of Care" /></SelectTrigger>
                <SelectContent>{CATEGORIES.filter((c) => c[2]).map(([, t, k]) => <SelectItem key={k} value={k}>{t}</SelectItem>)}</SelectContent></Select>
              <div className="h-11 rounded-md border bg-white flex items-center gap-2 px-3 text-sm text-[#626A5F]"><Calendar className="h-4 w-4" />When do you need services? (next step)</div>
              <Button type="submit" className="w-full h-12 rounded-lg zv-btn-gold font-bold" data-testid="quick-search-submit">Search Providers</Button>
            </form>
          </div>
        </div>
        <div>
          <div className="h-px w-16 bg-[#D4AF63] mb-6" />
          <h2 className="font-display text-3xl sm:text-5xl font-semibold text-[#FAF8F2]">Join the Zunvel Community</h2>
          <p className="text-[#FAF8F2]/80 text-lg mt-4 max-w-md">Be part of a growing network dedicated to better care, better opportunities and healthier communities.</p>
          <div className="mt-8"><GoldBtn to="/register" testid="join-create-account-btn">Create Your Account</GoldBtn></div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div data-testid="home-page" className="bg-[#FAF8F2] overflow-x-clip">
      <Hero /><CategoryStrip /><Journeys /><Why /><Services /><Verified /><Passport /><Providers /><Join />
    </div>
  );
}
